// Project workspace: shell + Overview + Assets, ported from the design source
// (screens-project.jsx) and bound to the live services: projects, assets,
// publications and publish versions.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Outlet, useParams } from 'react-router-dom';

import { useMapEditorStore, type StudioProject } from '../../engine';
import { AppStage, AppTopNav, timeAgo } from '../app/screens-app';
import { EdConfirmDialog } from '../editor/editor-panels';
import { CenteredLoading } from '../components/ui-shared';
import { EmptyState } from '../components/EmptyState';
import { GeneratedCover } from '../components/GeneratedCover';
import { ImageSlot } from '../components/ImageSlot';
import { Link, navigate } from '../design/router';
import { SectionNav, SectionNavItem } from '../components/SectionNav';
import {
  deleteAsset,
  loadProject,
  renameAsset,
  saveProjectConfig,
} from '../persistence/projectRepository';
import { loadModeDocument } from '../services/docs';
import { GAMESETUP_DOC_FALLBACK, type GameSetupDoc } from '../services/gameSetup';
import {
  getPublication,
  listPublicationVersions,
  type Publication,
  type PublicationVersion,
} from '../services/publish';

/* ── icons (12-16px line glyphs) ──────────────────────────────────────── */
export function PjIcon({ d, size = 15 }: { d: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d={d} stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export const PJ_ICONS: Record<string, string> = {
  overview: 'M2.5 2.5h4.5v4.5H2.5zM9 2.5h4.5v4.5H9zM2.5 9h4.5v4.5H2.5zM9 9h4.5v4.5H9z',
  assets: 'M8 1.8l5.5 3.1v6.2L8 14.2l-5.5-3.1V4.9L8 1.8zM2.5 4.9L8 8m0 0l5.5-3.1M8 8v6.2',
  publish: 'M8 13.5V5m0 0L4.5 8.5M8 5l3.5 3.5M2.5 2.5h11',
  versions: 'M8 4.5V8l2.5 1.5M14 8A6 6 0 112 8a6 6 0 0112 0z',
  wiki: 'M2.5 3.5h4a2 2 0 012 2v8a1.8 1.8 0 00-1.8-1.8H2.5zM13.5 3.5h-4a2 2 0 00-2 2v8a1.8 1.8 0 011.8-1.8h4.2zM5.2 6.2h2M5.2 8.6h2M8.8 6.2h2M8.8 8.6h2',
  players:
    'M6 7.5a2.25 2.25 0 100-4.5 2.25 2.25 0 000 4.5zM2 13.5c0-2.2 1.8-4 4-4s4 1.8 4 4M10.5 7.3a2.25 2.25 0 100-4.3M11 9.6c1.7.4 3 1.9 3 3.9',
  settings:
    'M8 10.2a2.2 2.2 0 100-4.4 2.2 2.2 0 000 4.4zM13 8c0-.4 0-.8-.1-1.2l1.4-1-1.2-2.1-1.6.6c-.6-.5-1.2-.9-2-1.1L9.2 1.5H6.8l-.3 1.7c-.8.2-1.4.6-2 1.1l-1.6-.6-1.2 2.1 1.4 1C3 7.2 3 7.6 3 8s0 .8.1 1.2l-1.4 1 1.2 2.1 1.6-.6c.6.5 1.2.9 2 1.1l.3 1.7h2.4l.3-1.7c.8-.2 1.4-.6 2-1.1l1.6.6 1.2-2.1-1.4-1c.1-.4.1-.8.1-1.2z',
  back: 'M9.5 12.5L5 8l4.5-4.5',
  edit: 'M11.3 2.2l2.5 2.5L5.5 13H3v-2.5l8.3-8.3z',
  copy: 'M5.5 5.5h8v8h-8zM10.5 5.5v-3h-8v8h3',
  external: 'M6.5 3.5H3v9.5h9.5V9.5M9.5 2.5H13.5V6.5M13.5 2.5L7.5 8.5',
  trash: 'M2.5 4.5h11M6 4.5V2.8a1 1 0 011-1h2a1 1 0 011 1v1.7M3.8 4.5l.6 8.4a1 1 0 001 .9h5.2a1 1 0 001-.9l.6-8.4M6.5 7.2v4M9.5 7.2v4',
};

/* ── workspace data (route layout provider) ───────────────────────────── */
type GameSetupSummary = Pick<GameSetupDoc, 'type' | 'teams' | 'region'>;

const GAME_SETUP_SUMMARY_FALLBACK: GameSetupSummary = {
  type: GAMESETUP_DOC_FALLBACK.type,
  teams: GAMESETUP_DOC_FALLBACK.teams,
  region: GAMESETUP_DOC_FALLBACK.region,
};

export interface WorkspaceData {
  projectId: string;
  project: StudioProject | null;
  publication: Publication | null;
  versions: PublicationVersion[];
  /** The project's own Game Setup type/team-format/region - not a fixed
   * placeholder, falls back to the same default Game Setup itself uses for
   * an unsaved doc (GAMESETUP_DOC_FALLBACK). Loaded once here, alongside
   * project/publication/versions, rather than as a separate per-tab fetch:
   * every tab that reads this rides the same `loading` gate those already
   * do, so a Solo project's Players/Publish tabs never flash the
   * multiplayer-flavoured UI before this resolves. */
  gameSetup: GameSetupSummary;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

const WorkspaceContext = createContext<WorkspaceData | null>(null);

export function useWorkspace(): WorkspaceData {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) throw new Error('useWorkspace must be used inside WorkspaceProvider.');
  return ctx;
}

/** Layout route for /app/project/:id/* - loads the project once per id. */
export function WorkspaceProvider() {
  const { id = '' } = useParams();
  const [project, setProject] = useState<StudioProject | null>(null);
  const [publication, setPublication] = useState<Publication | null>(null);
  const [versions, setVersions] = useState<PublicationVersion[]>([]);
  const [gameSetup, setGameSetup] = useState<GameSetupSummary>(GAME_SETUP_SUMMARY_FALLBACK);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        // The gamesetup doc has its own catch, same reasoning as versions
        // below: a project that never opened Game Setup, or a transient
        // failure fetching it, must not take down the whole workspace load
        // (nor even flip to the error screen) over what is, everywhere it's
        // used, a soft-fallback field.
        const [proj, pub, setupDoc] = await Promise.all([
          loadProject(id),
          getPublication(id),
          loadModeDocument<GameSetupDoc>(id, 'gamesetup').catch(() => null),
        ]);
        const vers = pub ? await listPublicationVersions(id).catch(() => []) : [];
        if (cancelled) return;
        if (!proj) {
          setError('Project not found.');
        } else {
          setProject(proj);
          setPublication(pub);
          setVersions(vers);
          setGameSetup({
            type: setupDoc?.data.type ?? GAME_SETUP_SUMMARY_FALLBACK.type,
            teams: setupDoc?.data.teams ?? GAME_SETUP_SUMMARY_FALLBACK.teams,
            region: setupDoc?.data.region ?? GAME_SETUP_SUMMARY_FALLBACK.region,
          });
        }
      } catch {
        if (!cancelled) setError('Could not load the project.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [id, nonce]);

  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  const value = useMemo<WorkspaceData>(
    () => ({ projectId: id, project, publication, versions, gameSetup, loading, error, refresh }),
    [id, project, publication, versions, gameSetup, loading, error, refresh],
  );

  return (
    <WorkspaceContext.Provider value={value}>
      <Outlet />
    </WorkspaceContext.Provider>
  );
}

/** Selector over the shared workspace's Game Setup summary (see
 * WorkspaceData.gameSetup), plus the derived multiplayer/competitive flags
 * every consumer needs - computed once here rather than re-derived at each
 * call site. `multiplayer` mirrors GameRulesPanel's own `type !== 'Solo'`
 * gate (editor-panels.tsx) for the same field; `competitive` narrows further
 * to the one type (Versus) whose match data actually carries a meaningful
 * raiders/wardens score. */
export function useGameSetupSummary(): GameSetupSummary & { multiplayer: boolean; competitive: boolean } {
  const { gameSetup } = useWorkspace();
  return {
    ...gameSetup,
    multiplayer: gameSetup.type !== 'Solo',
    competitive: gameSetup.type === 'Versus',
  };
}

const STORAGE_LIMIT_BYTES = 10 * 1024 ** 3;

/** Concurrent players included per plan, shared across every one of a
 * tenant's published games - fixed platform-wide today, not a per-project
 * figure (see the Multiplayer guide). */
const PLATFORM_INCLUDED_POOL = 50;

export function formatBytes(bytes: number): string {
  if (bytes >= 1024 ** 3) return (bytes / 1024 ** 3).toFixed(1) + ' GB';
  if (bytes >= 1024 ** 2) return (bytes / 1024 ** 2).toFixed(1) + ' MB';
  if (bytes >= 1024) return Math.round(bytes / 1024) + ' KB';
  return bytes + ' B';
}

/* ── unsaved-changes guard ────────────────────────────────────────────── */
// A project tab with its own dirty state (currently just the wiki page
// editor) registers it here so PjShell's navigation - the rail's tab links,
// "All projects" and "Open editor" - and a real tab close/refresh both ask
// before discarding unsaved work. Module-level rather than context-based: it
// only needs to answer "is it safe to navigate right now" at click time, and
// every project tab shares one PjShell instance so there is only ever one
// active guard regardless of which tab set it.
let unsavedChangesMessage: string | null = null;

export function useUnsavedChangesGuard(
  dirty: boolean,
  message = 'You have unsaved changes. Leave without saving?',
): void {
  useEffect(() => {
    unsavedChangesMessage = dirty ? message : null;
    return () => {
      unsavedChangesMessage = null;
    };
  }, [dirty, message]);

  useEffect(() => {
    function onBeforeUnload(e: BeforeUnloadEvent) {
      if (!unsavedChangesMessage) return;
      e.preventDefault();
      e.returnValue = '';
    }
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);
}

/** True if it is safe to navigate away right now - nothing is dirty, or the
 * user just confirmed leaving anyway. Callers wire this into a click handler
 * and prevent the default navigation when it returns false. */
export function confirmNavigation(): boolean {
  if (!unsavedChangesMessage) return true;
  return window.confirm(unsavedChangesMessage);
}

/* ── shell ────────────────────────────────────────────────────────────── */
export function PjShell({ tab, children }: { tab: string; children: ReactNode }) {
  const { projectId, project, publication, loading, error } = useWorkspace();
  const items = [
    { id: 'overview', label: 'Overview' },
    { id: 'assets', label: 'Assets' },
    { id: 'publish', label: 'Publish' },
    { id: 'versions', label: 'Versions' },
    { id: 'wiki', label: 'Wiki' },
    { id: 'players', label: 'Players' },
    { id: 'settings', label: 'Settings' },
  ];
  const live = publication !== null && publication.visibility !== 'private';
  const usedBytes = (project?.assets ?? []).reduce((sum, a) => sum + (a.sizeBytes ?? 0), 0);
  const usedPct = Math.min(100, (usedBytes / STORAGE_LIMIT_BYTES) * 100);

  return (
    <AppStage fill>
      <AppTopNav />
      <div className="pj-shell">
        <SectionNav label="Project">
          <SectionNavItem
            to="/app"
            active={false}
            style={{ color: 'var(--ss-text-3)', marginBottom: 10 }}
            onClick={(e) => { if (!confirmNavigation()) e.preventDefault(); }}
          >
            <PjIcon d={PJ_ICONS.back!} /> All projects
          </SectionNavItem>
          <div style={{ padding: '0 12px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              <span style={{ fontSize: 15.5, fontWeight: 600 }}>{project?.meta.name ?? (loading ? '…' : 'Project')}</span>
              {live && <LiveBadge />}
            </div>
            <Link
              to={`/app/editor/${projectId}/edit`}
              className="ss-btn ss-btn--primary ss-btn--sm"
              style={{ justifyContent: 'center' }}
              onClick={(e) => { if (!confirmNavigation()) e.preventDefault(); }}
            >
              <PjIcon d={PJ_ICONS.edit!} size={13} /> Open editor
            </Link>
          </div>
          {items.map((it) => (
            <SectionNavItem
              key={it.id}
              to={`/app/project/${projectId}/${it.id}`}
              active={tab === it.id}
              onClick={(e) => { if (tab !== it.id && !confirmNavigation()) e.preventDefault(); }}
            >
              <PjIcon d={PJ_ICONS[it.id]!} /> {it.label}
            </SectionNavItem>
          ))}
          <div style={{ marginTop: 'auto', padding: '14px 12px 4px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--ss-fs-2xs)', color: 'var(--ss-text-3)' }}>
              <span>Storage</span>
              <span style={{ fontFamily: 'var(--ss-sans)', fontWeight: 500 }}>
                {formatBytes(usedBytes)} / 10 GB
              </span>
            </div>
            <div style={{ height: 3, borderRadius: 999, background: 'var(--ss-border)' }}>
              <div style={{ width: `${usedPct}%`, height: '100%', borderRadius: 999, background: 'var(--ss-accent)' }} />
            </div>
          </div>
        </SectionNav>
        <div className="pj-main">
          {error ? (
            <div className="pj-page">
              <p style={{ color: 'var(--ss-danger)', fontSize: 14 }} role="alert">
                {error}
              </p>
            </div>
          ) : loading && !project ? (
            // Only the true first load (nothing fetched yet) blocks on this -
            // a background refresh() (rename/delete/upload, cover set) keeps
            // showing the still-valid data instead of flashing every tab's
            // "nothing here yet" empty copy, which used to render literally
            // (Overview said "Not published yet" / "No assets yet" while the
            // real answer just hadn't arrived).
            <div className="pj-page">
              <CenteredLoading text="Loading project…" />
            </div>
          ) : (
            children
          )}
        </div>
      </div>
    </AppStage>
  );
}

function PjSpark({ pts, color = 'var(--ss-accent)' }: { pts: number[]; color?: string }) {
  const W = 96,
    H = 28,
    max = Math.max(...pts);
  const step = W / (pts.length - 1);
  const d = pts
    .map((p, i) => (i === 0 ? 'M' : 'L') + (i * step).toFixed(1) + ' ' + (H - (p / max) * (H - 3) - 1.5).toFixed(1))
    .join(' ');
  return (
    <svg width={W} height={H} viewBox={'0 0 ' + W + ' ' + H} aria-hidden="true" style={{ display: 'block' }}>
      <path d={d} fill="none" stroke={color} strokeWidth="1.6" strokeLinejoin="round" opacity="0.85" />
    </svg>
  );
}

export function PjStat({ n, l, delta, dim, spark }: { n: string; l: string; delta?: string; dim?: boolean; spark?: number[] }) {
  return (
    <div className="stat-card">
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
        <span className="n" style={dim ? { color: 'var(--ss-text-2)' } : {}}>
          {n}
        </span>
        {spark && <PjSpark pts={spark} />}
      </div>
      <span className="l">{l}</span>
      {delta && <span className="d" style={{ color: delta.startsWith('+') ? 'var(--ss-success)' : 'var(--ss-text-3)' }}>{delta}</span>}
    </div>
  );
}

/** The small "● LIVE" pill - shown next to the project name in the rail and
 * on the live row of the Versions timeline. Was hand-copied inline in both
 * places with identical styling. */
export function LiveBadge({ label = 'LIVE' }: { label?: string }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        fontFamily: 'var(--ss-sans)',
        fontWeight: 500,
        fontSize: 9,
        letterSpacing: '0.14em',
        color: 'var(--ss-success)',
        border: '1px solid color-mix(in srgb, var(--ss-success) 25%, transparent)',
        borderRadius: 999,
        padding: '2px 8px',
      }}
    >
      <span style={{ width: 4, height: 4, borderRadius: '50%', background: 'var(--ss-success)' }} />
      {label}
    </span>
  );
}

export function CopyField({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <input className="ss-input" readOnly value={value} style={{ fontFamily: 'var(--ss-mono)', fontSize: 12, padding: '9px 12px' }} />
      <button
        className="ss-btn ss-btn--ghost ss-btn--sm"
        style={{ borderRadius: 9, minWidth: 70 }}
        onClick={() => {
          void navigator.clipboard?.writeText(value).catch(() => {});
          setCopied(true);
          setTimeout(() => setCopied(false), 1600);
        }}
      >
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}

/* ── Overview ─────────────────────────────────────────────────────────── */
const EDITOR_MODES_3D: [string, string][] = [
  ['Edit', 'edit'], ['Map', 'map'], ['Environment', 'environment'],
  ['Model', 'model'], ['Characters', 'characters'], ['Animation', 'animation'],
  ['Logic', 'logic'], ['HUD', 'hud'], ['Audio', 'audio'],
  ['FX', 'fx'], ['Cutscenes', 'cutscene'], ['Data', 'data'], ['Game', 'game'],
];
const EDITOR_MODES_2D: [string, string][] = [
  ['Edit', 'edit'], ['Map', 'map'], ['Environment', 'environment'],
  ['Pixel', 'pixel'],
  ['Logic', 'logic'], ['HUD', 'hud'], ['Audio', 'audio'],
  ['Data', 'data'], ['Game', 'game'],
];

/** The project's key art: shows the uploaded cover (or a generated one) and
 * uploads a replacement through the image asset pipeline. The chosen cover
 * rides in project config, so publish snapshots carry it to the play page. */
function CoverUploader() {
  const { projectId, project, refresh } = useWorkspace();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const cover = project?.config.cover;

  async function onPick(file: File) {
    if (!project) return;
    setBusy(true);
    setError(null);
    try {
      const { uploadCoverImage } = await import('../assets/coverUpload');
      const record = await uploadCoverImage(projectId, file);
      await saveProjectConfig(projectId, { ...project.config, cover: record });
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ position: 'relative' }}>
      {cover ? (
        <ImageSlot shape="rounded" radius={11} src={cover.url} alt="Cover image" style={{ width: '100%', height: 150, display: 'block' }} />
      ) : (
        <GeneratedCover name={project?.meta.name ?? 'Game'} style={{ borderRadius: 11 }} />
      )}
      <button
        className="ss-btn ss-btn--ghost ss-btn--sm"
        disabled={busy}
        onClick={() => inputRef.current?.click()}
        style={{ position: 'absolute', bottom: 10, right: 10, background: 'var(--ss-glass)', backdropFilter: 'blur(8px)' }}
      >
        {busy ? 'Uploading…' : cover ? 'Replace cover' : 'Set cover'}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) void onPick(file);
        }}
      />
      {error && <p style={{ position: 'absolute', left: 0, right: 0, bottom: -22, fontSize: 11.5, color: 'var(--ss-danger)' }} role="alert">{error}</p>}
    </div>
  );
}

export function ScreenProjectOverview() {
  const { projectId, project, publication, versions } = useWorkspace();
  const live = publication !== null && publication.visibility !== 'private';
  const publishedAgo = publication?.updated_at ? timeAgo(new Date(publication.updated_at).getTime()) : null;
  const visibilityLabel = publication
    ? publication.visibility === 'private'
      ? 'Off'
      : publication.visibility.charAt(0).toUpperCase() + publication.visibility.slice(1)
    : 'Not published';
  const recent = versions.slice(0, 3);
  const assets = project?.assets ?? [];
  const usedBytes = assets.reduce((sum, a) => sum + (a.sizeBytes ?? 0), 0);
  const { region, multiplayer } = useGameSetupSummary();

  return (
    <PjShell tab="overview">
      <div className="pj-page">
        <div className="pj-page-head">
          <div>
            <h1>Overview</h1>
            <p>
              {publication
                ? `Live on v${publication.version}${publishedAgo ? ` · published ${publishedAgo}` : ''}`
                : 'Not published yet'}
            </p>
          </div>
          {live && publication && (
            <a className="ss-btn ss-btn--ghost ss-btn--sm" href={`https://${publication.url.replace(/^https?:\/\//, '')}`} target="_blank" rel="noreferrer">
              <PjIcon d={PJ_ICONS.external!} size={12} /> View live game
            </a>
          )}
        </div>

        <div className="set-card" style={{ marginBottom: 18 }}>
          <div className="ss-grid-collapse" style={{ display: 'grid', gridTemplateColumns: '264px 1fr', gap: 24, alignItems: 'center' }}>
            <CoverUploader />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingRight: 12 }}>
              <div>
                <span className="ss-field-label">Public play URL</span>
                <div style={{ marginTop: 8 }}>
                  {publication ? (
                    <CopyField value={publication.url.replace(/^https?:\/\//, '')} />
                  ) : (
                    <span className="hint">Publish to claim a play URL.</span>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 26px', fontSize: 12.5, color: 'var(--ss-text-3)' }}>
                <span style={{ whiteSpace: 'nowrap' }}>
                  Visibility <strong style={{ color: 'var(--ss-text-2)', fontWeight: 500 }}>{visibilityLabel}</strong>
                </span>
                {multiplayer && (
                  <span style={{ whiteSpace: 'nowrap' }}>
                    Pool <strong style={{ color: 'var(--ss-text-2)', fontWeight: 500 }}>{PLATFORM_INCLUDED_POOL} players</strong>
                  </span>
                )}
                <span style={{ whiteSpace: 'nowrap' }}>
                  Region <strong style={{ color: 'var(--ss-text-2)', fontWeight: 500 }}>{region}</strong>
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="ss-grid-collapse" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 18 }}>
          <PjStat n={String(assets.length)} l="Assets" />
          <PjStat n={String(versions.length)} l={versions.length === 1 ? 'Version published' : 'Versions published'} />
          <PjStat n={formatBytes(usedBytes)} l="Storage used" />
          <PjStat n={live ? 'Live' : 'Off'} l="Status" dim={!live} />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 18, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: 'var(--ss-text-3)', marginRight: 4 }}>Jump back in</span>
          <SectionNav label="Jump back in" orientation="horizontal">
            {(project?.config.projection === '2D' ? EDITOR_MODES_2D : EDITOR_MODES_3D).map(([label, id]) => (
              <SectionNavItem
                key={id}
                to={`/app/editor/${projectId}/${id}`}
                active={false}
                orientation="horizontal"
                style={{ textTransform: 'none', letterSpacing: '0.02em', fontSize: 12, padding: '5px 14px' }}
              >
                {label}
              </SectionNavItem>
            ))}
          </SectionNav>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <h3 style={{ fontSize: 15, fontWeight: 600 }}>Recent versions</h3>
          <Link to={`/app/project/${projectId}/versions`} style={{ fontSize: 13, color: 'var(--ss-accent)', fontWeight: 500 }}>
            All versions
          </Link>
        </div>
        {recent.length === 0 && (
          <p className="ss-hint" style={{ padding: '12px 0' }}>
            Versions appear here once you publish.
          </p>
        )}
        {recent.map((r) => (
          <div key={r.version} className="ss-row">
            <span
              style={{
                fontFamily: 'var(--ss-sans)',
                fontWeight: 500,
                fontSize: 12,
                color: r.live ? 'var(--ss-accent)' : 'var(--ss-text-3)',
                width: 32,
              }}
            >
              v{r.version}
            </span>
            <span style={{ flex: 1, color: 'var(--ss-text-2)' }}>{r.note || 'Published'}</span>
            <span style={{ fontSize: 12, color: 'var(--ss-text-3)', whiteSpace: 'nowrap' }}>
              {(r.created_at ? timeAgo(new Date(r.created_at).getTime()) : '') + (r.live ? ' · Live' : '')}
            </span>
          </div>
        ))}
      </div>
    </PjShell>
  );
}

/* ── Assets ───────────────────────────────────────────────────────────── */
interface AssetView {
  id: string;
  name: string;
  size: string;
  when: string;
  used?: string;
  kind: 'glb' | 'audio' | 'texture';
}

function AssetRow({ a, onPlace, onRename, onDelete }: {
  a: AssetView;
  onPlace: () => void;
  onRename: (name: string) => void;
  onDelete: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(a.name);
  function commit() {
    setEditing(false);
    const next = draft.trim().replace(/\.glb$/i, '');
    if (next && next !== a.name.replace(/\.glb$/i, '')) onRename(next);
  }
  return (
    <div className="ss-row">
      <span
        style={{
          width: 34,
          height: 34,
          borderRadius: 9,
          flexShrink: 0,
          background: 'var(--ss-accent-soft)',
          color: 'var(--ss-accent)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <PjIcon d={a.kind === 'glb' ? PJ_ICONS.assets! : PJ_ICONS.overview!} size={15} />
      </span>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {editing ? (
          <input
            className="ss-input"
            value={draft}
            autoFocus
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit();
              if (e.key === 'Escape') { setDraft(a.name); setEditing(false); }
            }}
            style={{ padding: '4px 8px', fontSize: 12.5, borderRadius: 7, maxWidth: 280 }}
          />
        ) : (
          <button
            onClick={() => { setDraft(a.name.replace(/\.glb$/i, '')); setEditing(true); }}
            title="Rename asset"
            style={{ border: 'none', background: 'transparent', padding: 0, textAlign: 'left', fontFamily: 'var(--ss-sans)', fontWeight: 500, fontSize: 12.5, color: 'var(--ss-text)', cursor: 'text', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >{a.name}</button>
        )}
        <span style={{ fontSize: 11.5, color: 'var(--ss-text-3)' }}>{a.used ? 'Used in ' + a.used : 'Not placed yet'}</span>
      </div>
      <span
        style={{
          fontFamily: 'var(--ss-sans)',
          fontWeight: 500,
          fontSize: 10,
          letterSpacing: '0.1em',
          color: 'var(--ss-text-3)',
          border: '1px solid var(--ss-border-soft)',
          borderRadius: 999,
          padding: '3px 9px',
        }}
      >
        {a.kind === 'glb' ? 'GLB' : a.kind === 'audio' ? 'AUDIO' : 'IMAGE'}
      </span>
      <span style={{ fontSize: 12, color: 'var(--ss-text-3)', width: 64, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{a.size}</span>
      <span style={{ fontSize: 12, color: 'var(--ss-text-3)', width: 86, textAlign: 'right', whiteSpace: 'nowrap' }}>{a.when}</span>
      <button className="ss-btn ss-btn--ghost row-action" style={{ padding: '4px 10px', fontSize: 12, borderRadius: 7 }} onClick={onPlace}>
        Place
      </button>
      <button
        className="ss-btn ss-btn--ghost row-action"
        title={'Delete ' + a.name}
        aria-label={'Delete ' + a.name}
        style={{ padding: '4px 9px', fontSize: 12, borderRadius: 7, color: 'var(--ss-danger)' }}
        onClick={onDelete}
      >
        <PjIcon d={PJ_ICONS.trash!} size={13} />
      </button>
    </div>
  );
}

export function ScreenProjectAssets() {
  const { projectId, project, refresh } = useWorkspace();
  const [filter, setFilter] = useState('All');
  const [uploading, setUploading] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState<AssetView | null>(null);

  const usedCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const inst of project?.map.objectInstances ?? []) {
      if (inst.assetId.startsWith('glb_') || inst.assetId.startsWith('asset_')) {
        counts.set(inst.assetId, (counts.get(inst.assetId) ?? 0) + 1);
      }
    }
    return counts;
  }, [project]);

  const assets: AssetView[] = useMemo(
    () =>
      (project?.assets ?? []).map((a) => {
        const used = usedCounts.get(a.id) ?? 0;
        return {
          id: a.id,
          name: a.kind === 'glb' && !a.name.endsWith('.glb') ? `${a.name}.glb` : a.name,
          size: formatBytes(a.sizeBytes ?? 0),
          when: timeAgo(a.createdAt),
          used: used > 0 ? `${used} place${used === 1 ? '' : 's'}` : undefined,
          kind: a.kind,
        };
      }),
    [project, usedCounts],
  );

  const shown =
    filter === 'Models' ? assets.filter((a) => a.kind === 'glb')
    : filter === 'Images' ? assets.filter((a) => a.kind === 'texture')
    : filter === 'Audio' ? assets.filter((a) => a.kind === 'audio')
    : assets;
  const totalBytes = (project?.assets ?? []).reduce((sum, a) => sum + (a.sizeBytes ?? 0), 0);

  async function pickAndUpload() {
    setUploadError(null);
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.glb';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      setUploading(file.name);
      try {
        const { uploadGlbAsset } = await import('../assets/glbUpload');
        await uploadGlbAsset(projectId, file);
        refresh();
      } catch (err) {
        setUploadError(err instanceof Error ? err.message : 'Upload failed.');
      } finally {
        setUploading(null);
      }
    };
    input.click();
  }

  return (
    <PjShell tab="assets">
      <div className="pj-page">
        <div className="pj-page-head">
          <div>
            <h1>Assets</h1>
            <p>
              {assets.length} asset{assets.length === 1 ? '' : 's'} · {formatBytes(totalBytes)} · models and sprites now, audio coming
            </p>
          </div>
          <button className="ss-btn ss-btn--primary ss-btn--sm" onClick={pickAndUpload} disabled={uploading !== null}>
            + Upload .glb
          </button>
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 22 }}>
          {['All', 'Models', 'Images', 'Audio'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              style={{
                border: '1px solid ' + (f === filter ? 'var(--ss-accent-border)' : 'var(--ss-border-soft)'),
                background: f === filter ? 'var(--ss-accent-soft)' : 'transparent',
                color: f === filter ? 'var(--ss-accent)' : 'var(--ss-text-2)',
                borderRadius: 999,
                padding: '6px 14px',
                fontSize: 12.5,
                fontWeight: 500,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 7,
              }}
            >
              {f}
            </button>
          ))}
        </div>
        {uploading && (
          <div className="ss-row" style={{ borderTop: 'none', background: 'var(--ss-panel)', borderRadius: 12, padding: '13px 14px', marginBottom: 6 }}>
            <span
              style={{
                width: 34,
                height: 34,
                borderRadius: 9,
                flexShrink: 0,
                border: '1px dashed var(--ss-border)',
                color: 'var(--ss-text-3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <PjIcon d={PJ_ICONS.publish!} size={14} />
            </span>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 7 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5 }}>
                <span style={{ fontFamily: 'var(--ss-sans)', fontWeight: 500, color: 'var(--ss-text-2)' }}>{uploading}</span>
                <span style={{ fontFamily: 'var(--ss-sans)', fontWeight: 500, fontSize: 11, color: 'var(--ss-accent)' }}>Uploading…</span>
              </div>
              <div style={{ height: 3, borderRadius: 999, background: 'var(--ss-border)' }}>
                <div style={{ width: '100%', height: '100%', borderRadius: 999, background: 'var(--ss-accent)', opacity: 0.5 }} />
              </div>
            </div>
          </div>
        )}
        {uploadError && (
          <p style={{ color: 'var(--ss-danger)', fontSize: 13, marginBottom: 10 }} role="alert">
            {uploadError}
          </p>
        )}
        <div className="col-head">
          <span style={{ width: 34 }} />
          <span style={{ flex: 1 }}>Asset</span>
          <span>Type</span>
          <span style={{ width: 64, textAlign: 'right' }}>Size</span>
          <span style={{ width: 86, textAlign: 'right' }}>Added</span>
          <span style={{ width: 52 }} />
        </div>
        {shown.map((a) => (
          <AssetRow
            key={a.id}
            a={a}
            onPlace={() => {
              // Arm click-to-place and land in the editor with it pending.
              useMapEditorStore.getState().setPendingAssetId(a.id);
              navigate(`/app/editor/${projectId}/edit`);
            }}
            onRename={(name) => {
              void renameAsset(projectId, a.id, name).then(refresh).catch(() => setUploadError('Rename failed.'));
            }}
            onDelete={() => setConfirmingDelete(a)}
          />
        ))}
        {shown.length === 0 && !uploading && (
          assets.length === 0 ? (
            <EmptyState
              icon={<PjIcon d={PJ_ICONS.assets!} size={19} />}
              title="No assets yet"
              hint="Upload a .glb model to place in your scene - sprites and audio are already listed here once you add them from the editor."
              action={
                <button className="ss-btn ss-btn--primary ss-btn--sm" onClick={pickAndUpload}>
                  + Upload .glb
                </button>
              }
            />
          ) : (
            <p className="ss-hint" style={{ padding: '14px 0' }}>No assets match this filter.</p>
          )
        )}
        <p className="ss-hint" style={{ marginTop: 26 }}>
          Validated on upload: max 10 MiB, single scene, triangulated meshes. Uploads register instantly for placement.
        </p>
      </div>
      {confirmingDelete && (
        <EdConfirmDialog
          title={`Delete ${confirmingDelete.name}?`}
          body={`This removes the file permanently.${confirmingDelete.used ? ` It is used in ${confirmingDelete.used}; placed copies will show as missing.` : ''}`}
          confirmLabel="Delete asset"
          onConfirm={() => {
            const target = confirmingDelete;
            setConfirmingDelete(null);
            void deleteAsset(projectId, target.id).then(refresh).catch(() => setUploadError('Delete failed.'));
          }}
          onCancel={() => setConfirmingDelete(null)}
        />
      )}
    </PjShell>
  );
}
